import { Injectable } from '@angular/core';
import { FormControl, Validators } from '@angular/forms';
import { FormValidatorsService } from '../../../services/form-validators/form-validators.service';
import { without } from 'lodash';

type TNonEmailFormControls = {
  [controlName: string]: FormControl;
};

type TEmailFormControls = {
  [controlName: string]: {
    formControl: FormControl;
    emailType: string;
    placeholder: string;
  };
};

@Injectable()
export class ShelterClusterUserModalShellService {
  constructor(private formValidatorService: FormValidatorsService) {}

  /**
   * Returns form controls for non email fields
   *
   * @param {*} { userBasic}
   * @returns {TNonEmailFormControls}
   * @memberof ClusterUserModalShellService
   */
  public getNonEmailFormControls({ userBasic }): TNonEmailFormControls {
    const firstName = (userBasic && userBasic.firstName) || '';
    const firstNameControl = this._getFormControlForName(firstName);
    const lastName = (userBasic && userBasic.lastName) || '';
    const lastNameControl = this._getFormControlForName(lastName);
    const title = (userBasic && userBasic.title) || '';
    const titleControl = this._getFormControlForTitle(title);
    return {
      firstNameControl,
      lastNameControl,
      titleControl,
    };
  }

  /**
   * Returns form controls for email fields
   *
   * @param {*} { userBasic, clusterDetail, emailDupes }
   * @returns {TEmailFormControls}
   * @memberof ClusterUserModalShellService
   */
  public getEmailFormControls({ userBasic, clusterDetail, emailDupes }): TEmailFormControls {
    const requireEmailControl = this._getFormControlForEmail({
      userBasic,
      clusterDetail,
      emailDupes,
      isRequiredEmail: true,
    });
    let optionalEmailControl;
    if (clusterDetail.loginMethod.optional) {
      optionalEmailControl = this._getFormControlForEmail({
        userBasic,
        clusterDetail,
        emailDupes,
        isRequiredEmail: false,
      });
    }
    return {
      requireEmailControl,
      optionalEmailControl,
    };
  }

  /**
   *
   *
   * @private
   * @param {*} name
   * @returns {FormControl}
   * @memberof ClusterUserModalShellService
   */
  private _getFormControlForName(name): FormControl {
    const peopleNameValidator = this.formValidatorService.peopleNameValidatorCtr();
    const formControl = new FormControl(name, {
      validators: [Validators.required, peopleNameValidator],
    });
    return formControl;
  }

  /**
   *
   *
   * @private
   * @param {*} title
   * @returns {FormControl}
   * @memberof ClusterUserModalShellService
   */
  private _getFormControlForTitle(title): FormControl {
    return new FormControl(title);
  }

  /**
   *
   *
   * @private
   * @param {*} {
   *     userBasic,
   *     clusterDetail,
   *     emailDupes,
   *     isRequiredEmail,
   *   }
   * @returns {{
   *     formControl: FormControl;
   *     emailType: string;
   *     placeholder: string;
   *   }}
   * @memberof ClusterUserModalShellService
   */
  private _getFormControlForEmail({
    userBasic,
    clusterDetail,
    emailDupes,
    isRequiredEmail,
  }): {
    formControl: FormControl;
    emailType: string;
    placeholder: string;
  } {
    const gafeEmail = (userBasic && userBasic.gafeEmail) || '';
    const doeEmail = (userBasic && userBasic.doeEmail) || '';
    const dhsEmail = (userBasic && userBasic.dhsEmail) || '';
    const isShelterClusterTrustedUser = userBasic && userBasic.isShelterClusterTrustedUser;
    const { approvedDomains, loginMethod } = clusterDetail;
    const methodName = isRequiredEmail ? loginMethod.required : loginMethod.optional;
    const { emailType, placeholder, domains, initialVal } = this._getFormControlUiValues({
      methodName,
      approvedDomains,
      dhsEmail,
      gafeEmail,
      doeEmail,
    });
    let validators = [
      Validators.email,
      this.formValidatorService.emailDomainValidatorCtr({ domains }),
      this.formValidatorService.emailDupesValidatorCtr({ emailDupes }),
    ];
    if (isRequiredEmail) {
      validators = validators.concat(Validators.required);
    }
    const formControl = new FormControl(initialVal, validators);
    if (isShelterClusterTrustedUser === false) {
      formControl.disable();
    }
    return { formControl, emailType, placeholder };
  }

  /**
   *
   *
   * @private
   * @param {*} {
   *     methodName,
   *     approvedDomains,
   *     gafeEmail,
   *     doeEmail,
   *   }
   * @returns {{ emailType: string; placeholder: string; domains: string[]; initialVal: string }}
   * @memberof ClusterUserModalShellService
   */
  private _getFormControlUiValues({
    methodName,
    approvedDomains,
    dhsEmail,
    gafeEmail,
    doeEmail,
  }): { emailType: string; placeholder: string; domains: string[]; initialVal: string } {
    let domains;
    let emailType;
    let initialVal;
    switch (methodName) {
      case 'DHS':
        domains = ['dss.nyc.gov'];
        emailType = 'dhsEmail';
        initialVal = dhsEmail;
        break;
      case 'DOE':
        domains = ['schools.nyc.gov'];
        emailType = 'doeEmail';
        initialVal = doeEmail;
        break;
      case 'Google':
        const argArray = [approvedDomains].concat(['schools.nyc.gov']);
        domains = without.apply(this, argArray);
        emailType = 'gafeEmail';
        initialVal = gafeEmail;
      default:
        break;
    }
    const placeholder = domains.join(', ');
    return { emailType, placeholder, domains, initialVal };
  }

  /**
   *
   *
   * @param {*} { firstNameFormControl, lastNameFormControl }
   * @returns {string}
   * @memberof ClusterUserModalShellService
   */
  public getUserFullName({ firstNameFormControl, lastNameFormControl }): string {
    const first = firstNameFormControl.value;
    const last = lastNameFormControl.value;
    return `${first} ${last}`;
  }
}
